// ═══════════════════════════════════════════════
// 爱压 AI 操作模式 —— 命令目录
//
// 大模型 (或 / 命令模板) 只能从这里挑命令, 不能自己算工程数值:
// 真正干活的永远是各页面现有的按钮逻辑, 页面通过 useAgentCommands 注册实现。
// 目录是静态的 —— 页面没打开时也要让模型知道有这些能力, 执行时再跳过去。
//
// kind 决定要不要人工确认:
//   read / navigate  自动执行 (只看不改)
//   write / run      必须在面板里点"确认执行" (改方案 / 跑求解器)
// ═══════════════════════════════════════════════

export type AgentCommandKind = 'read' | 'navigate' | 'write' | 'run';

export type AgentParamSpec = {
  name: string;
  type: 'string' | 'number' | 'number[]' | 'enum';
  description: string;
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
};

export type AgentCommandSpec = {
  id: string;
  title: string;
  description: string;
  kind: AgentCommandKind;
  /** 命令所在页面; 为空表示全局可用 */
  page?: string;
  params?: AgentParamSpec[];
};

export const AGENT_PAGES: Array<{ value: string; label: string }> = [
  { value: '/dashboard', label: '仪表盘' },
  { value: '/datahub/manage', label: '数据清单' },
  { value: '/formation/layers', label: '分层数据' },
  { value: '/mechanics/profile', label: '参数剖面' },
  { value: '/stability/window', label: '安全泥浆窗口' },
  { value: '/stability/imaging', label: '成像测井解释' },
  { value: '/sweetspot', label: '甜点评价' },
  { value: '/fracture/designs', label: '方案总览' },
  { value: '/fracture/builder', label: '分段分簇' },
  { value: '/fracture/wellbore', label: '完井与射孔' },
  { value: '/fracture/materials', label: '材料设计' },
  { value: '/fracture/schedule', label: '泵注程序' },
  { value: '/fracture/lab', label: '物模实验库' },
  { value: '/fracsim/overview', label: '压裂模拟' },
  { value: '/fracsim/curves', label: '动态曲线' },
  { value: '/fracsim/history', label: '方案对比' },
];

export const AGENT_COMMANDS: AgentCommandSpec[] = [
  {
    id: 'nav.goto',
    title: '打开页面',
    description: '跳转到指定页面, 让用户看到后续操作。',
    kind: 'navigate',
    params: [{ name: 'page', type: 'enum', required: true, description: '目标页面', options: AGENT_PAGES }],
  },
  {
    id: 'page.status',
    title: '读取当前页面状态',
    description: '读取当前页面的方案、段、就绪状态等摘要, 用于判断下一步。',
    kind: 'read',
  },

  // ── 成像测井解释 ──
  {
    id: 'imaging.analyze',
    title: '识别成像测井',
    description: '用页面上已选的文件和参数识别成像测井 (已打开记录时按当前参数重新识别)。文件需用户在页面上选择。',
    kind: 'write',
    page: '/stability/imaging',
  },
  {
    id: 'imaging.publish',
    title: '发布成像测井裂缝',
    description: '把已人工确认的裂缝发布到天然裂缝表。',
    kind: 'write',
    page: '/stability/imaging',
  },

  // ── 物模实验库 ──
  {
    id: 'lab.recommend',
    title: '物模实验对标',
    description: '用物模实验库页面上填写的目标条件做对标分析, 返回相似实验与设计参考 (只读计算)。',
    kind: 'read',
    page: '/fracture/lab',
  },

  // ── 方案总览 ──
  {
    id: 'design.list',
    title: '列出压裂方案',
    description: '列出当前项目的压裂方案 (编号、名称、是否激活)。',
    kind: 'read',
    page: '/fracture/designs',
  },
  {
    id: 'design.ai_generate',
    title: 'AI 生成压裂方案',
    description: '打开「AI 生成方案」对话框并预填用户的补充要求 (只写用户说过的要求, 不写数值); 用户在对话框里确认后生成。',
    kind: 'write',
    page: '/fracture/new-design',
    params: [{ name: 'brief', type: 'string', description: '用户的补充要求原话, 可为空' }],
  },
  {
    id: 'design.activate',
    title: '激活压裂方案',
    description: '把指定编号的方案设为当前方案, 后续分段、完井、泵注、模拟都基于它。',
    kind: 'write',
    page: '/fracture/designs',
    params: [{ name: 'design_id', type: 'number', required: true, description: '方案编号 (design.list 返回的 id)' }],
  },

  // ── 分段分簇 ──
  {
    id: 'staging.smart_design',
    title: '智能分段',
    description: '打开当前方案的智能分段对话框; 用户在对话框里核对后自行确认部署。',
    kind: 'write',
    page: '/fracture/builder',
  },
  {
    id: 'staging.save',
    title: '保存分段分簇方案',
    description: '打开保存对话框保存当前分段分簇方案。',
    kind: 'write',
    page: '/fracture/builder',
  },

  // ── 完井与射孔 ──
  {
    id: 'completion.smart_design',
    title: '完井与射孔智能设计',
    description: '基于甜点、地层和类比案例为全部压裂段生成完井方式与射孔参数 (未保存)。',
    kind: 'write',
    page: '/fracture/wellbore',
  },
  {
    id: 'completion.save',
    title: '保存完井与射孔方案',
    description: '保存当前完井与射孔设计。',
    kind: 'write',
    page: '/fracture/wellbore',
  },

  // ── 材料 / 泵注 ──
  {
    id: 'materials.save',
    title: '保存材料设计',
    description: '保存当前压裂液与支撑剂设计。',
    kind: 'write',
    page: '/fracture/materials',
  },
  {
    id: 'pump.generate',
    title: '智能生成泵注程序',
    description: '为全部压裂段生成推荐泵注程序 (未保存, 生成后可人工修改)。',
    kind: 'write',
    page: '/fracture/schedule',
  },
  {
    id: 'pump.save',
    title: '保存泵注程序',
    description: '打开保存对话框保存泵注程序。',
    kind: 'write',
    page: '/fracture/schedule',
  },

  // ── 压裂模拟 ──
  {
    id: 'fracsim.select_design',
    title: '切换模拟方案',
    description: '在压裂模拟页切换到指定方案; 页面会自动用新方案启动全部段计算, 之后不必再 fracsim.run。',
    kind: 'run',
    page: '/fracsim/overview',
    params: [{ name: 'design_id', type: 'number', required: true, description: '方案编号' }],
  },
  {
    id: 'fracsim.select_stages',
    title: '选择模拟压裂段',
    description: '选择要模拟的压裂段; 不给段号表示全部段。',
    kind: 'write',
    page: '/fracsim/overview',
    params: [{ name: 'stages', type: 'number[]', description: '段号列表, 例如 [1,2,3]; 留空=全部' }],
  },
  {
    id: 'fracsim.run',
    title: '运行压裂模拟',
    description: '用当前选择的方案与压裂段启动求解器 (耗时操作)。',
    kind: 'run',
    page: '/fracsim/overview',
  },
];

export const findAgentCommand = (id: string) => AGENT_COMMANDS.find((c) => c.id === id);

export const agentPageLabel = (path: string) => AGENT_PAGES.find((p) => p.value === path)?.label ?? path;

export const needsConfirmation = (kind: AgentCommandKind) => kind === 'write' || kind === 'run';
